import React, { useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Building2, ShieldCheck, Sparkles, Lock } from "lucide-react";

const SPECIALIZATIONS = [
  "General Dentist",
  "Endodontist",
  "Prosthodontist",
  "Orthodontist",
  "Oral Surgeon",
  "Periodontist",
  "Pedodontist",
  "Dental Hygienist",
  "Dental Assistant",
];
const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Locum", "Internship"];
const EXPERIENCE = ["0-1 Years", "1-3 Years", "3-5 Years", "5-10 Years", "10+ Years"];

const initial = {
  clinic_name: "",
  contact_person: "",
  mobile: "",
  email: "",
  location: "",
  role: "",
  employment_type: "",
  experience: "",
  joining_date: "",
  requirements: "",
  agree: false,
};

async function postWithFallback(url, body, localKey) {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {}
  try {
    const list = JSON.parse(localStorage.getItem(localKey) || "[]");
    list.push({ ...body, submitted_at: new Date().toISOString() });
    localStorage.setItem(localKey, JSON.stringify(list));
  } catch {}
}

export default function HireForMeDrawer({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clinic_name.trim() || !form.contact_person.trim() || !form.email.trim()) {
      toast.error("Please fill in clinic name, contact person and email.");
      return;
    }
    if (!form.agree) {
      toast.error("Please accept the Terms of Service.");
      return;
    }
    setLoading(true);
    try {
      await postWithFallback("/api/staffing/hiring-requests", form, "dyntra_hire_for_me_v1");
      toast.success("Requirement submitted — we'll shortlist candidates within 48 hours.");
      setForm(initial);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="hire-for-me-drawer"
      >
        <SheetHeader className="pb-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-[#F3EFFC] flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-[#4B2AAD]" />
            </div>
            <div>
              <SheetTitle className="font-display text-lg">
                Hire For Me — Tell Us Who You Need
              </SheetTitle>
              <SheetDescription className="text-xs">
                Share your hiring requirement and we&apos;ll connect you with the
                best-matched dental professionals.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={submit} className="pt-5 space-y-4">
          <Field label="Clinic / Hospital Name" placeholder="Enter clinic or hospital name" value={form.clinic_name} onChange={(v) => set("clinic_name", v)} testid="hfm-clinic" />
          <Field label="Contact Person" placeholder="Enter full name" value={form.contact_person} onChange={(v) => set("contact_person", v)} testid="hfm-contact" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobile Number" placeholder="Enter mobile number" value={form.mobile} onChange={(v) => set("mobile", v)} testid="hfm-mobile" />
            <Field label="Email Address" placeholder="Enter email address" type="email" value={form.email} onChange={(v) => set("email", v)} testid="hfm-email" />
          </div>
          <Field label="Location" placeholder="City, area or full address" value={form.location} onChange={(v) => set("location", v)} testid="hfm-loc" />

          <Select
            label="Role / Specialization"
            placeholder="Select role or specialization"
            value={form.role}
            onChange={(v) => set("role", v)}
            options={SPECIALIZATIONS}
            testid="hfm-role"
          />
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Employment Type"
              placeholder="Select employment type"
              value={form.employment_type}
              onChange={(v) => set("employment_type", v)}
              options={EMPLOYMENT_TYPES}
              testid="hfm-emp"
            />
            <Select
              label="Experience Required"
              placeholder="Select experience"
              value={form.experience}
              onChange={(v) => set("experience", v)}
              options={EXPERIENCE}
              testid="hfm-exp"
            />
          </div>
          <Field
            label="Preferred Joining Date"
            type="date"
            value={form.joining_date}
            onChange={(v) => set("joining_date", v)}
            testid="hfm-date"
          />

          <div>
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Additional Requirements
            </label>
            <textarea
              value={form.requirements}
              onChange={(e) => set("requirements", e.target.value.slice(0, 500))}
              placeholder="Describe skills, certifications, equipment expertise, language preference, or any other specific requirements..."
              rows={4}
              className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm resize-none"
            />
            <p className="text-[10px] text-gray-400 text-right mt-1">
              {form.requirements.length}/500
            </p>
          </div>

          <label className="flex items-start gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={form.agree}
              onChange={(e) => set("agree", e.target.checked)}
              className="mt-0.5 accent-[#4B2AAD] h-4 w-4"
            />
            I agree to Dyntra&apos;s{" "}
            <a href="/policy/terms" className="text-[#4B2AAD] font-semibold">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/policy/privacy" className="text-[#4B2AAD] font-semibold">
              Privacy Policy
            </a>
            .
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="hfm-submit"
            className="dyntra-btn-primary w-full py-3"
          >
            {loading ? "Submitting..." : "Submit Requirement"}
          </button>

          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 text-center text-[10px] text-gray-500">
            <TrustCell icon={ShieldCheck} label="Verified professionals" sub="all profiles verified" />
            <TrustCell icon={Sparkles} label="Smart matching" sub="right professionals" />
            <TrustCell icon={Lock} label="Secure & confidential" sub="safe and never shared" />
          </div>

          <p className="text-[10px] text-gray-400 text-center">
            Your requirement is private and visible only to our hiring team.
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
      />
    </div>
  );
}

function Select({ label, value, onChange, placeholder, options, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm bg-white"
      >
        <option value="">{placeholder || "Select"}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function TrustCell({ icon: Icon, label, sub }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <Icon className="h-3.5 w-3.5 text-[#4B2AAD]" />
      <p className="text-[10px] font-bold text-gray-900 leading-none">{label}</p>
      <p className="text-[9px] text-gray-500 leading-none">{sub}</p>
    </div>
  );
}
